import { Link } from "react-router";
import { computeAccountingMonth } from "@/domain/accounting-period";
import { Decimal } from "@/domain/money";
import { computeMonth } from "@/finance/month";
import { formatEurPlain, formatMonth, formatMonthTitle, formatPercent, formatDate } from "@/lib/format";
import { BudgetMeter } from "@/ui/charts";
import { EmptyState, Figure, Notice, Panel } from "@/ui/primitives";
import { useCategoryTree, useFinanceIndex, usePaymentMethods, useSetting, useToday } from "../hooks";
import { useLiveQuery } from "dexie-react-hooks";
import { useState } from "react";
import { countPendingChanges } from "@/data/sync-package";
import { db } from "../hooks";

export function DashboardPage() {
  const { today, month } = useToday();
  const index = useFinanceIndex();
  const methods = usePaymentMethods();
  const tree = useCategoryTree();
  const pending = useLiveQuery(() => countPendingChanges(db), []);
  const lastBackup = useSetting<string | null>("backup.lastExportAt", null);
  const [openedAt] = useState(() => Date.now());

  if (!index || !tree || !methods) return <p className="text-muted">Caricamento…</p>;
  const m = computeMonth(index, month);
  const deferred = methods.filter((pm) => pm.accountingRule === "next_month");
  const topGroups = [...m.expenses.byGroup].sort((a, b) => new Decimal(b.actual).cmp(a.actual)).slice(0, 4);
  const hasAnyData = index.dataMonths.length > 0;
  const backupOld = hasAnyData && (!lastBackup || openedAt - Date.parse(lastBackup) > 30 * 86_400_000);

  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby="dash-month">
        <p className="text-muted">Mese contabile in corso</p>
        <h1 id="dash-month" className="font-ledger text-month text-petrol-strong">
          {formatMonthTitle(month)}
        </h1>
      </section>

      {tree.length === 0 ? (
        <Notice>
          Le categorie non sono ancora state importate. <Link to="/importa" className="font-semibold underline underline-offset-4">Importa il file Excel</Link> per
          avere categorie, storico, entrate, budget e saldo di partenza.
        </Notice>
      ) : null}

      <div className="grid grid-cols-2 gap-x-4 gap-y-5 rounded-xl border border-line bg-surface p-4 md:grid-cols-4 md:p-5">
        <Figure label="Entrate" amount={m.income.total} />
        <Figure label="Spese" amount={m.expenses.total} hint={`${m.expenses.count} movimenti`} />
        <Figure label="Risparmio" amount={m.savings.actual} signed hint={m.savings.rate ? `${formatPercent(m.savings.rate)} delle entrate` : undefined} />
        <Figure
          label="Budget residuo"
          amount={m.budget.hasBudget ? m.budget.remaining : "0"}
          hint={
            !m.budget.hasBudget
              ? "Nessun budget impostato"
              : new Decimal(m.budget.variance).isNegative()
                ? `Oltre il budget di ${formatEurPlain(new Decimal(m.budget.variance).neg())}`
                : `su ${formatEurPlain(m.budget.expenses)}`
          }
        />
      </div>

      <Panel title="Saldo" action={<Link to="/saldo" className="text-sm font-medium text-petrol">Dettagli</Link>}>
        <dl className="grid grid-cols-2 gap-y-1 text-sm sm:grid-cols-4">
          <dt className="text-muted">Inizio mese</dt>
          <dd className="amount text-right sm:text-left">{formatEurPlain(m.balance.opening)}</dd>
          <dt className="text-muted">Fine mese (finora)</dt>
          <dd className="amount text-right font-semibold sm:text-left">{formatEurPlain(m.balance.closing)}</dd>
        </dl>
        {!m.balance.anchored ? (
          <p className="mt-2 text-sm text-muted">
            Nessun saldo di partenza: <Link to="/saldo" className="font-medium text-petrol underline underline-offset-4">impostalo</Link> per vedere la posizione reale.
          </p>
        ) : null}
      </Panel>

      {deferred.length > 0 ? (
        <ul className="space-y-1 border-l-2 border-petrol pl-3 text-sm">
          {deferred.map((pm) => (
            <li key={pm.id}>
              Se paghi oggi con {pm.name}, la spesa conta in{" "}
              <strong className="font-semibold">{formatMonth(computeAccountingMonth({ purchaseDate: today, rule: pm.accountingRule }).accountingMonth)}</strong>.
            </li>
          ))}
        </ul>
      ) : null}

      <Panel title="Dove vanno i soldi" action={<Link to="/mese" className="text-sm font-medium text-petrol">Mese completo</Link>}>
        {topGroups.length === 0 ? (
          <EmptyState title="Nessuna spesa in questo mese">Usa il pulsante + per aggiungerne una.</EmptyState>
        ) : (
          <ul className="space-y-3">
            {topGroups.map((g) => (
              <li key={g.groupId}>
                <div className="flex justify-between gap-3 text-sm">
                  <span className="font-medium">{g.label}</span>
                  <span className="amount">
                    {formatEurPlain(g.actual)}
                    {new Decimal(g.budget).gt(0) ? <span className="text-muted"> / {formatEurPlain(g.budget)}</span> : null}
                  </span>
                </div>
                <BudgetMeter actual={Number(g.actual)} budget={Number(g.budget)} />
                {new Decimal(g.variance).isNegative() && new Decimal(g.budget).gt(0) ? <p className="text-xs text-over">Oltre il budget</p> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Spese più alte del mese">
        {m.expenses.largest.length === 0 ? (
          <p className="text-sm text-muted">Ancora nessuna spesa.</p>
        ) : (
          <ul className="divide-y divide-line">
            {m.expenses.largest.map((e) => (
              <li key={e.id} className="flex justify-between gap-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  {e.merchant ?? index.categories.get(e.categoryId)?.excelLabel}
                  <span className="text-muted"> — {formatDate(e.purchaseDate)}</span>
                </span>
                <span className="amount font-medium">{formatEurPlain(e.amountEur)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {(pending ?? 0) > 0 || backupOld ? (
        <div className="flex flex-col gap-2">
          {(pending ?? 0) > 0 ? (
            <Notice>
              {pending} modifiche da trasferire al PC. <Link to="/sync" className="font-semibold underline underline-offset-4">Esporta il pacchetto</Link>
            </Notice>
          ) : null}
          {backupOld ? (
            <Notice>
              {lastBackup ? "L'ultimo backup ha più di 30 giorni." : "Non hai ancora fatto un backup dei dati."}{" "}
              <Link to="/impostazioni" className="font-semibold underline underline-offset-4">Fai un backup</Link>
            </Notice>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
